import { getCurrentTimestamp, getCurrentDateString } from '../utils/timeUtils';

const ONLINE_STATUS_KEY = 'worker-online-status';
const LAST_RESET_KEY = 'last-online-reset-date';

interface OnlineStatusData {
  userId: number;
  isOnline: boolean;
  lastActivated: string;
}

export class OnlineStatusService {
  // Check if we need to reset online status (new day)
  private static checkAndResetDaily(): void {
    const today = getCurrentDateString();
    const lastReset = localStorage.getItem(LAST_RESET_KEY);
    
    if (lastReset !== today) {
      // It's a new day, reset all online statuses
      localStorage.removeItem(ONLINE_STATUS_KEY);
      localStorage.setItem(LAST_RESET_KEY, today);
      console.log('Daily online status reset performed');
    }
  }

  // Get all online statuses
  static getAllStatuses(): Record<number, OnlineStatusData> {
    this.checkAndResetDaily();
    
    const stored = localStorage.getItem(ONLINE_STATUS_KEY);
    if (!stored) {
      return {};
    }
    
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error parsing online status data:', error);
      return {};
    }
  }

  // Check if a specific user is online
  static isUserOnline(userId: number): boolean {
    const statuses = this.getAllStatuses();
    const userStatus = statuses[userId];
    
    if (!userStatus) {
      return false;
    }
    
    // Check if the status is from today
    const today = getCurrentDateString();
    const statusDate = userStatus.lastActivated.split('T')[0];
    
    return statusDate === today && userStatus.isOnline;
  }

  // Set a user as online
  static setUserOnline(userId: number): void {
    const statuses = this.getAllStatuses();
    
    statuses[userId] = {
      userId,
      isOnline: true,
      lastActivated: getCurrentTimestamp()
    };
    
    localStorage.setItem(ONLINE_STATUS_KEY, JSON.stringify(statuses));
    console.log(`User ${userId} set as online`);
  }

  // Set a user as offline
  static setUserOffline(userId: number): void {
    const statuses = this.getAllStatuses();
    
    if (statuses[userId]) {
      statuses[userId].isOnline = false;
      localStorage.setItem(ONLINE_STATUS_KEY, JSON.stringify(statuses));
      console.log(`User ${userId} set as offline`);
    }
  }

  // Activate a user (bring them online)
  static activateUser(userId: number, userName: string): boolean {
    try {
      this.setUserOnline(userId);
      
      // Record activation in table actions for notifications
      const activationAction = {
        id: `activation-${Date.now()}-${Math.random()}`,
        timestamp: getCurrentTimestamp(),
        workerName: userName,
        action: 'activated',
        tableName: 'System'
      };
      
      const storedActions = localStorage.getItem('table-actions');
      let actions = [];
      if (storedActions) {
        try {
          actions = JSON.parse(storedActions);
        } catch (error) {
          console.error('Error parsing table actions:', error);
        }
      }
      
      actions.push(activationAction);
      
      // Keep only last hour of actions
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const filteredActions = actions.filter((a: { timestamp: string }) => new Date(a.timestamp) > oneHourAgo);
      
      localStorage.setItem('table-actions', JSON.stringify(filteredActions));
      
      return true;
    } catch (error) {
      console.error('Error activating user:', error);
      return false;
    }
  }

  // Get online users for a specific date
  static getOnlineUsersForDate(date: string): number[] {
    const statuses = this.getAllStatuses();
    const onlineUsers: number[] = [];
    
    Object.values(statuses).forEach(status => {
      if (status.isOnline && status.lastActivated.split('T')[0] === date) {
        onlineUsers.push(status.userId);
      }
    });
    
    return onlineUsers;
  }

  // Force reset all statuses (admin function)
  static forceResetAllStatuses(): void {
    localStorage.removeItem(ONLINE_STATUS_KEY);
    localStorage.setItem(LAST_RESET_KEY, getCurrentDateString());
    console.log('All online statuses forcefully reset');
  }
}

export default OnlineStatusService;